import type { ProviderAccount } from '@/api/schemas';
import { FormField, fieldA11y } from '@/components/FormField';
import { ProblemAlert } from '@/components/ProblemAlert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { CONNECT_COPY, replaceCopy } from './providerAccountModel';
import { ProviderTokenVerify } from './ProviderTokenParts';
import { useReplaceTokenForm, type ReplaceTokenForm as Form } from './useReplaceTokenForm';

interface Props {
  account: ProviderAccount | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * Replace a provider account's token. With a stored credential the token is rotated in place
 * (`POST /credentials/{id}/rotate`: same id, every account using it follows); an account that never stored one (its
 * worker used its own environment token) gets a new credential attached (`POST /credentials`, then `PATCH` `credentialId`).
 * The new token is verified against the account first; it is sent once and never shown again.
 */
export function ReplaceTokenDialog({ account, onOpenChange }: Props) {
  return (
    <Dialog open={Boolean(account)} onOpenChange={onOpenChange}>
      <DialogContent data-testid="replace-token-dialog" aria-describedby="replace-token-desc">
        {account ? <ReplaceTokenForm key={account.id} account={account} onDone={() => onOpenChange(false)} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function TokenField({ f }: { f: Form }) {
  const { error } = f;
  const copy = CONNECT_COPY[f.kind];
  return (
    <FormField id="replace-token-value" label={`New ${copy.tokenLabel.toLowerCase()}`} required={!f.created} hint={copy.tokenHelp} error={error}>
      <Input data-testid="replace-token-value" type="password" value={f.token} onChange={(e) => f.setToken(e.target.value)} autoComplete="off" spellCheck={false} className="font-mono sm:text-[13px]" {...fieldA11y('replace-token-value', error, 'hint')} />
    </FormField>
  );
}

function ReplaceTokenForm({ account: opened, onDone }: { account: ProviderAccount; onDone: () => void }) {
  const f = useReplaceTokenForm(opened, onDone);
  const copy = replaceCopy(Boolean(f.account.credentialId));
  return (
    <form data-testid="replace-token-form" onSubmit={(e) => void f.onSubmit(e)} noValidate className="flex min-h-0 flex-col" aria-label={`Replace the token of ${f.account.name}`}>
      <DialogHeader>
        <DialogTitle>{copy.title} · {f.account.name}</DialogTitle>
        <DialogDescription id="replace-token-desc">{copy.description}</DialogDescription>
      </DialogHeader>
      <DialogBody className="flex flex-col gap-4">
        {f.created ? <p className="text-[13px] text-muted-foreground">The token is stored; saving attaches it to the account.</p> : null}
        <TokenField f={f} />
        {f.created ? null : <ProviderTokenVerify kind={f.kind} check={f.check} />}
        <ProblemAlert error={f.problem} testId="replace-token-problem" />
      </DialogBody>
      <DialogFooter>
        <Button variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" data-testid="replace-token-submit" loading={f.busy}>
          {copy.title}
        </Button>
      </DialogFooter>
    </form>
  );
}
